import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface UseAdminApiOptions {
  /** Custom response key. Default 'items'. e.g. 'agreements' for { agreements: [...] }. */
  responseKey?: string
  /** Optional query string params; pass-through to the GET URL. */
  query?: Record<string, string | number | boolean | undefined>
  /** Initial page size. Default 50. */
  initialLimit?: number
  /**
   * HTTP verb used by `update`. Default 'PATCH' (most admin routes — including
   * the conversation-engine routes — expose PATCH). Pass 'PUT' for routes that
   * use PUT (e.g. some campaign routes).
   */
  updateMethod?: 'PATCH' | 'PUT'
}

export interface PaginationState {
  total: number
  limit: number
  offset: number
  onChange: (next: { limit: number; offset: number }) => void
}

export interface SortState {
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onChange: (next: { sortBy: string; sortOrder: 'asc' | 'desc' }) => void
}

export interface UseAdminApiResult<T> {
  /** The list of items returned by the GET request, read from `response[responseKey]`. Pass to ResourceTable's `rows` prop. */
  items: T[]
  loading: boolean
  error: string | null
  refetch: () => void
  create: (data: unknown) => Promise<T>
  update: (id: string, data: unknown) => Promise<T>
  remove: (id: string) => Promise<void>
  restore: (id: string) => Promise<void>
  /** Pagination state + onChange handler — matches ResourceTable v2 `pagination` prop. */
  pagination: PaginationState
  /** Sort state + onChange handler — matches ResourceTable v2 `sort` prop. */
  sort: SortState
}

interface ListResponse {
  [key: string]: unknown
}

/**
 * Generic CRUD hook for tenant-admin resource pages.
 *
 * @param basePath - API path segment, e.g. `/skills` or `/agents`.
 *   The hook calls `GET /api{basePath}` and expects `{ items: T[] }` by default,
 *   or `{ [responseKey]: T[] }` when `opts.responseKey` is provided.
 *
 * Pagination + sort state is managed internally; both are forwarded as query
 * params on every GET. Callers that don't use `pagination`/`sort` are
 * unaffected — the extra params are silently ignored by existing endpoints.
 */
export function useAdminApi<T>(
  basePath: string,
  opts: UseAdminApiOptions = {},
): UseAdminApiResult<T> {
  const responseKey = opts.responseKey ?? 'items'
  const updateMethod = opts.updateMethod ?? 'PATCH'

  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Increment to trigger a re-fetch without recreating the effect callback
  const [fetchKey, setFetchKey] = useState(0)

  // Pagination state
  const [pageState, setPageState] = useState<{ limit: number; offset: number; total: number }>({
    limit: opts.initialLimit ?? 50,
    offset: 0,
    total: 0,
  })

  // Sort state
  const [sortState, setSortState] = useState<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>({})

  // Keep a ref so the cleanup callback can cancel in-flight requests
  const cancelledRef = useRef(false)

  // Stable serialisation of opts.query so the effect dep list doesn't thrash
  const queryJson = JSON.stringify(opts.query)

  useEffect(() => {
    cancelledRef.current = false
    setLoading(true)
    setError(null)

    void (async () => {
      try {
        // Build URL with query params
        const params = new URLSearchParams()

        // 1. Caller-supplied static query params
        const query = opts.query
        if (query) {
          for (const [k, v] of Object.entries(query)) {
            if (v !== undefined) params.set(k, String(v))
          }
        }

        // 2. Pagination — always forwarded (overrides any same-key caller param)
        params.set('limit', String(pageState.limit))
        params.set('offset', String(pageState.offset))

        // 3. Sort — only forwarded when defined
        if (sortState.sortBy) {
          params.set('sortBy', sortState.sortBy)
          params.set('sortOrder', sortState.sortOrder ?? 'asc')
        }

        const url = `${basePath}?${params.toString()}`

        const res = await api.get<ListResponse>(url)
        if (!cancelledRef.current) {
          setItems((res[responseKey] as T[]) ?? [])
          // Update total if the response includes it (backward-compat: may be absent)
          if (typeof res['total'] === 'number') {
            setPageState((p) => ({ ...p, total: res['total'] as number }))
          }
        }
      } catch (err) {
        if (!cancelledRef.current) {
          const message =
            err instanceof Error ? err.message : `Failed to load ${basePath}`
          setError(message)
        }
      } finally {
        if (!cancelledRef.current) {
          setLoading(false)
        }
      }
    })()

    return () => {
      cancelledRef.current = true
    }
    // queryJson is a stable serialisation of opts.query — safe to use instead of opts.query
  }, [basePath, responseKey, fetchKey, queryJson, pageState.limit, pageState.offset, sortState.sortBy, sortState.sortOrder])

  const refetch = useCallback(() => {
    setFetchKey((k) => k + 1)
  }, [])

  const create = useCallback(
    async (data: unknown): Promise<T> => {
      const item = await api.post<T>(basePath, data)
      setItems((prev) => [...prev, item])
      return item
    },
    [basePath],
  )

  const update = useCallback(
    async (id: string, data: unknown): Promise<T> => {
      const item =
        updateMethod === 'PUT'
          ? await api.put<T>(`${basePath}/${id}`, data)
          : await api.patch<T>(`${basePath}/${id}`, data)
      setItems((prev) => prev.map((it) => (getItemId(it) === id ? item : it)))
      return item
    },
    [basePath, updateMethod],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`${basePath}/${id}`)
      // Optimistically reflect the soft-delete; a refetch will bring real data
      setItems((prev) =>
        prev.map((it) =>
          getItemId(it) === id
            ? markDeleted(it)
            : it,
        ),
      )
    },
    [basePath],
  )

  const restore = useCallback(
    async (id: string): Promise<void> => {
      await api.post(`${basePath}/${id}/restore`)
      setItems((prev) =>
        prev.map((it) =>
          getItemId(it) === id
            ? markRestored(it)
            : it,
        ),
      )
    },
    [basePath],
  )

  const pagination: PaginationState = {
    total: pageState.total,
    limit: pageState.limit,
    offset: pageState.offset,
    onChange: (next) => setPageState((p) => ({ ...p, limit: next.limit, offset: next.offset })),
  }

  const sort: SortState = {
    ...(sortState.sortBy !== undefined ? { sortBy: sortState.sortBy } : {}),
    ...(sortState.sortOrder !== undefined ? { sortOrder: sortState.sortOrder } : {}),
    onChange: (next) => setSortState({ sortBy: next.sortBy, sortOrder: next.sortOrder }),
  }

  return { items, loading, error, refetch, create, update, remove, restore, pagination, sort }
}

// ---------------------------------------------------------------------------
// Helpers — safely read/write common fields without `any`
// ---------------------------------------------------------------------------

type WithId = { id: string }
type WithDeletedAt = { deletedAt?: string | null }
type WithStatus = { status?: string }

function getItemId(item: unknown): string {
  return (item as WithId).id
}

function markDeleted<T>(item: T): T {
  const cast = item as WithDeletedAt & WithStatus
  const result = { ...cast }
  if ('deletedAt' in result) {
    result.deletedAt = new Date().toISOString()
  }
  if ('status' in result) {
    result.status = 'deleted'
  }
  return result as unknown as T
}

function markRestored<T>(item: T): T {
  const cast = item as WithDeletedAt & WithStatus
  const result = { ...cast }
  if ('deletedAt' in result) {
    result.deletedAt = null
  }
  if ('status' in result) {
    result.status = 'active'
  }
  return result as unknown as T
}

// ---------------------------------------------------------------------------
// Alias
// ---------------------------------------------------------------------------

/** Alias for {@link useAdminApi} — preferred name in compliance/platform plans. */
export const useAdminResource = useAdminApi
